<script setup>
import { Icon } from '@dashhudson/dashing-ui/icon';
import { LottieAnimation } from '@dashhudson/dashing-ui/lottie-animation';
import useVuelidate from '@vuelidate/core';
import { maxLength, required } from '@vuelidate/validators';
import { useInterval } from '@vueuse/core';
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

import { getCdnAssetUrl } from '@/assets';
import Button from '@/components/foundation/Button.vue';
import FormItem from '@/components/foundation/form/FormItem.vue';
import {
  ACTION_TYPES,
  CONVERSATION_TEXT_BOX_WARNING,
  INTENT_TYPES,
  OLD_INTENT_TOOLTIP,
  PROGRESSIVE_UPDATE_MESSAGES,
  REQUEST_CONTEXT_BUTTON_IDENTIFIERS,
  TEXTBOX_PLACEHOLDERS,
  TEXTBOX_UPGRADE_TOOLTIP,
} from '@/components/VisionAi/constants';
import { buildRequestContext, usePageFilters } from '@/components/VisionAi/utils/request-context';
import WarningBubble from '@/components/VisionAi/WarningBubble.vue';
import { VISION_AI_SENDER } from '@/config';
import { env } from '@/env';
import { useFlagStore } from '@/stores/flag';
import { useIdentityStore } from '@/stores/identity';
import { useVisionAiStore } from '@/stores/vision-ai';
import { useVisionAiConversationsStore } from '@/stores/vision-ai-conversations';
import { useVisionAiMessagesStore } from '@/stores/vision-ai-messages';
import { KEYDOWN_EVENT_KEYS } from '@/utils/keyEvents';
import { colours } from '@/ux/colours';

const INPUT_MAX_LENGTH = 500;
const INPUT_WARNING_LENGTH = 400;

const visionAiStore = useVisionAiStore();
const visionAiConversationsStore = useVisionAiConversationsStore();
const visionAiMessagesStore = useVisionAiMessagesStore();
const identityStore = useIdentityStore();
const flagStore = useFlagStore();
const route = useRoute();

const pageFilters = usePageFilters();

const userInput = ref('');
const textInputRef = ref(null);
const defaultTextAreaHeight = ref('2rem');
const textAreaFocused = ref(false);

const {
  counter: progressiveCounter,
  pause: pauseProgressive,
  resume: resumeProgressive,
  reset: resetProgressive,
} = useInterval(100, { controls: true });
pauseProgressive();

const {
  counter: ellipsisCounter,
  pause: pauseEllipsis,
  resume: resumeEllipsis,
  reset: resetEllipsis,
} = useInterval(500, { controls: true });
pauseEllipsis();

const currentPhase = ref('START');
const currentMessageIndex = ref(0);
const shuffledProcessingMessages = ref([]);
const nextUpdateTime = ref(0);
const ellipsisDots = ref(0);

function getRandomUpdateTime() {
  return Math.floor(Math.random() * 2000) + 6000;
}

function resetState() {
  currentPhase.value = 'START';
  currentMessageIndex.value = 0;
  ellipsisDots.value = 0;
  shuffledProcessingMessages.value = [...PROGRESSIVE_UPDATE_MESSAGES.PROCESSING].sort(
    () => Math.random() - 0.5,
  );
  nextUpdateTime.value = Date.now() + getRandomUpdateTime();
}

function updateProgressivePlaceholder() {
  if (
    !visionAiStore.loadingMessageInCurrentConversation ||
    visionAiConversationsStore.summariesSelected
  )
    return;

  const now = Date.now();
  let lastMessage = false;
  if (now >= nextUpdateTime.value && !lastMessage) {
    if (currentPhase.value === 'START') {
      currentMessageIndex.value += 1;
      if (currentMessageIndex.value >= PROGRESSIVE_UPDATE_MESSAGES.START.length) {
        currentPhase.value = 'PROCESSING';
        currentMessageIndex.value = 0;
      }
    } else if (currentPhase.value === 'PROCESSING') {
      currentMessageIndex.value += 1;
      if (currentMessageIndex.value >= shuffledProcessingMessages.value.length) {
        currentPhase.value = 'PACKAGING';
        currentMessageIndex.value = 0;
      }
    } else if (currentPhase.value === 'PACKAGING') {
      if (currentMessageIndex.value < PROGRESSIVE_UPDATE_MESSAGES.PACKAGING.length - 1) {
        currentMessageIndex.value += 1;
      } else {
        lastMessage = true;
      }
    }
    nextUpdateTime.value = now + getRandomUpdateTime();
  }
}

const props = defineProps({
  isExpanded: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const validations = {
  userInput: {
    required,
    maxLength: maxLength(INPUT_MAX_LENGTH),
  },
};

const v$ = useVuelidate(validations, { userInput });

const invalid = computed(() => v$.value.userInput.$invalid);

const showLegacyIcon = computed(() => env.isProduction);

const inputLengthMessage = computed(() => {
  const userInputLength = userInput.value?.length;
  if (userInputLength >= INPUT_WARNING_LENGTH) {
    return `${userInputLength}/${INPUT_MAX_LENGTH} characters`;
  }
  return null;
});

const userHitMaxLength = computed(() => userInput.value?.length === INPUT_MAX_LENGTH);

const sphereAnimationProps = computed(() => ({
  animationLink: getCdnAssetUrl('animations/vision-ai-sphere-blue.json'),
  width: '1.25rem',
  height: '1.25rem',
  renderer: 'canvas',
}));

const placeholderText = computed(() => {
  let baseText = '';

  if (visionAiStore.loadingMessageInCurrentConversation) {
    if (currentPhase.value === 'START') {
      baseText = PROGRESSIVE_UPDATE_MESSAGES.START[currentMessageIndex.value];
    } else if (currentPhase.value === 'PROCESSING') {
      baseText = shuffledProcessingMessages.value[currentMessageIndex.value];
    } else if (currentPhase.value === 'PACKAGING') {
      baseText = PROGRESSIVE_UPDATE_MESSAGES.PACKAGING[currentMessageIndex.value];
    }

    const dots = '.'.repeat(ellipsisDots.value);
    return `${baseText}${dots}`;
  }

  if (visionAiConversationsStore.summariesSelected) {
    return TEXTBOX_PLACEHOLDERS.SUMMARIES;
  }
  return TEXTBOX_PLACEHOLDERS.DEFAULT;
});

const buttonTooltip = computed(() =>
  visionAiStore.loadingMessageInCurrentConversation ? 'Response is being generated' : null,
);

const disabled = computed(
  () => props.disabled || visionAiConversationsStore.currentConversationHasOldIntent,
);

const blockUserInput = computed(
  () => disabled.value || visionAiStore.loadingMessageInCurrentConversation,
);

const formTooltip = computed(() => {
  if (props.disabled)
    return { content: TEXTBOX_UPGRADE_TOOLTIP, boundary: 'viewport', placement: 'top' };
  if (visionAiConversationsStore.currentConversationHasOldIntent) {
    return { content: OLD_INTENT_TOOLTIP, boundary: 'viewport', placement: 'top' };
  }
  return null;
});

async function handleSubmitUserInput(event) {
  event.preventDefault();

  if (invalid.value || blockUserInput.value) return;
  let conversationId = visionAiConversationsStore.currentConversationId;

  visionAiConversationsStore.addLoadingConversationIds(conversationId);
  if (visionAiConversationsStore.summariesSelected) {
    visionAiStore.startNewChat();
  }

  if (!conversationId) {
    const conversation = await visionAiConversationsStore.createConversation();
    conversationId = conversation?.id;
  }
  const currentBrandId = identityStore.currentBrand?.id ?? 0;
  const intentType =
    flagStore.ready && flagStore.flags?.visionAiUnifiedAgent
      ? INTENT_TYPES.UNIFIED
      : INTENT_TYPES.METRIC_ANALYTICS;
  const intentQuery = { currentBrandId };
  const requestContext = buildRequestContext(route, currentBrandId, pageFilters.value);
  await visionAiMessagesStore.createMessage({
    conversationId,
    sender: VISION_AI_SENDER.USER,
    message: userInput.value,
    intentType,
    intentQuery,
    actionType: ACTION_TYPES.QUESTION,
    usedPrompt: false,
    requestContext,
  });
  userInput.value = '';
  v$.value.userInput.$touch();
}

const showWarningBubble = computed(
  () => Boolean(userInput.value?.length) && visionAiConversationsStore.summariesSelected,
);

function onInput(e) {
  const element = e.target;
  setTimeout(() => {
    if (element.scrollHeight <= 90) {
      element.style.overflowY = 'hidden';
      element.style.height = e.target.scrollHeight <= 44 ? defaultTextAreaHeight.value : 'auto';
    } else {
      // If the text length exceeds 3 lines.
      element.style.overflowY = 'auto';
    }
  }, 0);
}

function handleFocusTextArea() {
  if (!textInputRef.value) return;
  textInputRef.value.style.overflowY = 'hidden';
  textInputRef.value.style.height = defaultTextAreaHeight.value;
  if (props.disabled) return;
  textInputRef.value.focus();
}

function handleArrowUp(event) {
  event.preventDefault();

  // Only update the user input if the conversation is open and the user hasn't typed anything yet
  if (visionAiConversationsStore.currentConversationId && !userInput.value) {
    userInput.value = visionAiMessagesStore.messages.findLast(
      (message) => message.sender === VISION_AI_SENDER.USER,
    ).message;
    v$.value.userInput.$touch();
  }
}

function handleKeydown(event) {
  if (event.shiftKey && event.key === KEYDOWN_EVENT_KEYS.ENTER) {
    event.stopPropagation();
  } else if (event.key === KEYDOWN_EVENT_KEYS.ENTER) {
    handleSubmitUserInput(event);
  } else if (event.key === KEYDOWN_EVENT_KEYS.ARROW_UP && userInput.value?.length === 0) {
    // Only handle arrow up if the user has not typed anything yet
    handleArrowUp(event);
  } else {
    onInput(event);
  }
}

watch(userInput, (to) => {
  if (!to) {
    textInputRef.value.style.height = defaultTextAreaHeight.value;
  }
});

watch(
  () => visionAiConversationsStore.currentConversationId,
  () => {
    handleFocusTextArea();
  },
);

watch(
  () => visionAiStore.loadingMessageInCurrentConversation,
  (isLoading) => {
    if (isLoading) {
      resetState();
      resumeProgressive();
      resumeEllipsis();
    } else {
      pauseProgressive();
      resetProgressive();
      pauseEllipsis();
      resetEllipsis();
    }
  },
);

watch(progressiveCounter, () => {
  updateProgressivePlaceholder();
});

watch(ellipsisCounter, () => {
  ellipsisDots.value = (ellipsisDots.value + 1) % 4;
});

onMounted(() => {
  handleFocusTextArea();
});

defineExpose(
  process.env.NODE_ENV === 'test'
    ? {
        handleSubmitUserInput,
        invalid,
        inputLengthMessage,
        showWarningBubble,
        userHitMaxLength,
        blockUserInput,
      }
    : {},
);
</script>

<template>
  <form v-tooltip="formTooltip">
    <WarningBubble
      v-if="showWarningBubble"
      :text="CONVERSATION_TEXT_BOX_WARNING"
      hide-resource-link
    />

    <FormItem
      class="form-item !mt-0 !rounded-3xl !border !border-solid !bg-[--background-0]"
      :class="{
        '!mt-4': showWarningBubble,
        '!border-transparent': !textAreaFocused && !userHitMaxLength,
        '!border-[--border] !shadow-heavy': textAreaFocused && !userHitMaxLength,
        '!border-[--error-500] !shadow-heavy': userHitMaxLength,
        '!cursor-not-allowed !opacity-50': disabled,
      }"
    >
      <div class="!flex !items-center">
        <LottieAnimation
          v-if="showLegacyIcon"
          class="!ml-3 !h-[1.5rem] !w-[1.5rem]"
          v-bind="sphereAnimationProps"
        />
        <Icon v-else name="vision-ai" size="20" class="dash-fill-icon-vision-ai !ml-3" />
        <textarea
          ref="textInputRef"
          v-model="userInput"
          data-test="text-area"
          :disabled="disabled"
          :placeholder="placeholderText"
          :maxlength="INPUT_MAX_LENGTH"
          class="!min-h-10 !resize-none !border-none !px-2 !pt-[0.7rem] disabled:!cursor-not-allowed"
          @keydown="handleKeydown"
          @focus="() => (textAreaFocused = true)"
          @blur="() => (textAreaFocused = false)"
        />
        <Button
          v-tooltip="buttonTooltip"
          data-test="submit-user-input"
          :class="[
            '!ml-2 !mr-1 !h-[--space-30] !w-[--space-34] !rounded-full !bg-[--brand-primary] !p-[--space-5]',
            { '!h-[--space-32] !w-[--space-32]': props.isExpanded },
          ]"
          :loading="visionAiStore.loadingMessageInCurrentConversation"
          :disabled="blockUserInput"
          :icon-name="'prompt-icon'"
          :icon-color="colours.BASIC.WHITE"
          :icon-hover-color="colours.BASIC.WHITE"
          icon-size="xxsmall"
          @click.prevent="handleSubmitUserInput"
        />
      </div>
    </FormItem>
    <p
      v-if="inputLengthMessage"
      class="text-small !pl-3 !pt-2.5"
      :class="{ '!text-[--error-500]': userHitMaxLength }"
    >
      {{ inputLengthMessage }}
    </p>
  </form>
</template>

<style lang="postcss" scoped>
textarea {
  &::placeholder {
    -webkit-text-fill-color: var(--text-secondary);
  }
}
</style>
